import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { KeycloakAuthGuard, KeycloakService } from 'keycloak-angular';
import { ContextProvider } from '../../services/context-provider/context.provider';
import { ContextService } from '../../../ui-shared-components.services';
import { AuthenticationService } from '@govalta-emu/keycloak-auth-service';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationGuard extends KeycloakAuthGuard {
  constructor(
    protected readonly router: Router,
    protected readonly keycloakService: KeycloakService,
    protected readonly contextService: ContextService,
    private contextProvider: ContextProvider,
    protected readonly authenticationService: AuthenticationService
  ) {
    super(router, keycloakService);
  }

  public async isAccessAllowed(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    //also need to check if token is expired
    const checkToken = await this.authenticationService.checkRefreshToken();

    // Force the user to log in if currently unauthenticated.
    if (!this.isAuthenticated() || checkToken === null) {
      //There is a bug in keycloak where the last URL is being replace (not assigned) causing back button from keycloak to go to the page prior to the one expected to be rendered:
      //https://github.com/mauriciovigolo/keycloak-angular/issues/209
      //keycloak-js library is using replace instead of assign causing the browser back button to go to page previous to the last page prior to keycloak redirect
      //putting this in here as a fix to ensure the back click from keycloak goes to the correct page.
      window.history.pushState(window.history.state, document.title, window.location.href);
      const forceIdp = route?.data?.forceIdp;
      const url = state.url;
      let redirectUrl = window.location.origin + url;

      //now check if forceUrlRedirect parameter exists on parent or child route
      //if it exists, override the redirectUrl with this parameter.
      //this allows us to force a redirect for a specific route on login
      let forceUrlRedirect = route?.data?.forceUrlRedirect ?? ''; //by default use forceUrlRedirect from the root if there is a value
      const currentPath = url.split('?')[0].split('#')[0].split('/').filter(Boolean).pop();
      if (currentPath) {
        const root = route.root;
        const target = this.findRouteByPath(root, currentPath);
        const forceUrlRedirectChild = target?.data?.['forceUrlRedirect'];
        if (forceUrlRedirectChild) {
          forceUrlRedirect = forceUrlRedirectChild;
        }
        if (forceUrlRedirect) {
          if (forceUrlRedirect.startsWith('/')) {
            //if relative path, append to origin
            redirectUrl = window.location.origin + forceUrlRedirect;
          } else if (/^https?:\/\//i.test(forceUrlRedirect)) {
            // allow absolute URLs (if intentional)
            redirectUrl = forceUrlRedirect;
          }
        }
      }
      this.authenticationService.signIn(redirectUrl, forceIdp);
      this.contextProvider.setCurrentUser(null);
      return false;
    }

    if (this.isAuthenticated()) {
      const user = await this.contextService.getContextUserProfile(false);
      this.contextProvider.setCurrentUser(user);
    } else {
      this.contextProvider.setCurrentUser(null);
    }
    return true;
  }

  private isAuthenticated() {
    if (this.authenticated === null) {
      return false;
    }
    return this.authenticated;
  }

  //recursively check children to look for a matching path and grab the data for the target path
  private findRouteByPath(snapshot: ActivatedRouteSnapshot, targetPath: string): ActivatedRouteSnapshot | null {
    if (snapshot.routeConfig?.path === targetPath) {
      return snapshot;
    }
    for (const child of snapshot.children) {
      const match = this.findRouteByPath(child, targetPath);
      if (match) return match;
    }
    return null;
  }
}
